import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import { useAuth } from '../hooks/useAuth';
import { getBackblazeDownloadUrl } from '../config/backblaze';
import MediaCarousel, { buildMediaList } from './MediaCarousel';

const fmt = (n) => Number(n ?? 0).toLocaleString('ar-DZ');

const C = {
  green: '#16a34a',
  red:   '#dc2626',
  blue:  '#2563eb',
  gold:  '#d97706',
};

export default function ProviderPublicProfileScreen() {
  const { providerId } = useParams();
  const navigate       = useNavigate();
  const { user }       = useAuth();

  const [provider,      setProvider]      = useState(null);
  const [services,      setServices]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [isFav,         setIsFav]         = useState(false);
  const [favLoading,    setFavLoading]    = useState(false);
  const [isClient,      setIsClient]      = useState(false);
  const [purchased,     setPurchased]     = useState(new Set());
  const [sending,       setSending]       = useState(false);
  const [snack,         setSnack]         = useState(null);
  const [dialog,        setDialog]        = useState(null);
  const [loginRequiredDialog, setLoginRequiredDialog] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [payMethod,     setPayMethod]     = useState('cash');
  const [qty,           setQty]           = useState(1);
  const [clientBalance, setClientBalance] = useState(0);
  const [activeImg,     setActiveImg]     = useState(0);
  const [showAppBanner, setShowAppBanner] = useState(false);

  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const playStoreLink = 'https://play.google.com/store/apps/details?id=com.fares.khidma';
  const appDeepLink = `https://khidmadz.shop/provider-public/${providerId}`;

  const handleOpenApp = () => {
    if (isAndroid) {
      const intentUrl = `intent://khidmadz.shop/provider-public/${providerId}#Intent;scheme=https;package=com.fares.khidma;end`;
      window.location.href = intentUrl;
      setTimeout(() => {
        window.location.href = playStoreLink;
      }, 2000);
    } else {
      window.location.href = appDeepLink;
      setTimeout(() => {
        window.location.href = playStoreLink;
      }, 2000);
    }
  };

  const handleCloseBanner = () => {
  setShowAppBanner(false);
};

  useEffect(() => {
  load();
  if (isMobile) {
    setShowAppBanner(true);
  }
}, [providerId, user?.id]);

  async function load() {
    setLoading(true); setError(null);
    try {
      const p = await supabaseService.getProviderById(providerId);
      if (!p) { setError('لم يتم العثور على هذا المزود'); setLoading(false); return; }
      const svcs = await supabaseService.getServicesForProvider(p.id);
      let cli = false, fav = false;
      const bought = new Set();
      if (user?.id) {
        cli = user.id !== p.id;
        if (cli) {
          try {
            const c = await supabaseService.getClientById(user.id);
            if (c) setClientBalance(c.availableBalance ?? c.available_balance ?? 0);
          } catch {}
          try {
            const favs = JSON.parse(localStorage.getItem('favorites') || '[]');
            fav = favs.includes(p.id);
          } catch {}
          if (p.isDigitalProducts || p.is_digital_products) {
            for (const s of svcs) {
              if (!s.id) continue;
              try {
                const hp = await supabaseService.hasClientPurchased({ clientId: user.id, serviceId: s.id });
                if (hp) bought.add(s.id);
              } catch {}
            }
          }
        }
      }
      setProvider(p); setServices(svcs);
      setIsClient(cli); setIsFav(fav); setPurchased(bought);
    } catch { setError('حدث خطأ أثناء تحميل البيانات'); }
    finally  { setLoading(false); }
  }

  function showSnack(msg, ok = true) {
    setSnack({ msg, ok });
    setTimeout(() => setSnack(null), 3200);
  }

  async function toggleFav() {
    if (!provider || favLoading) return;
    setFavLoading(true);
    const favs = JSON.parse(localStorage.getItem('favorites') || '[]');
    const next = isFav ? favs.filter(id => id !== provider.id) : [...favs, provider.id];
    localStorage.setItem('favorites', JSON.stringify(next));
    setIsFav(!isFav);
    showSnack(!isFav ? 'تمت الإضافة إلى المفضلة' : 'تمت الإزالة من المفضلة', !isFav);
    setFavLoading(false);
  }

  function confirmRequest(service) {
    const isDigital = provider?.isDigitalProducts || provider?.is_digital_products;
    if (!user?.id || user.id === 'guest' || user.isGuest) {
      // حفظ رابط الصفحة الحالية للعودة إليها بعد تسجيل الدخول
      sessionStorage.setItem('pending_provider_page', window.location.pathname);
      setLoginRequiredDialog(true);
      return;
    }
    if (user.id === provider?.id) { showSnack('لا يمكنك الطلب من نفسك', false); return; }
    if (!isClient)                { showSnack('أنت مزولد، لا يمكنك الطلب', false); return; }
    if (!provider?.isVerified && !provider?.is_verified) {
      showSnack('لا يمكن إرسال طلب لمزود غير موثّق', false); return;
    }
    setActiveService(service); setQty(1); setPayMethod('cash');
    setDialog(isDigital ? 'buy' : 'request');
  }

  async function sendRequest() {
    if (sending) return;
    setSending(true);
    const total = (activeService?.price ?? 0) * qty;
    if (payMethod === 'wallet' && clientBalance < total) {
      showSnack(`رصيدك غير كافٍ (${fmt(clientBalance)} د.ج متاح)`, false);
      setSending(false); return;
    }
    try {
      const reqId = await supabaseService.createRequest({
        clientId: user.id, providerId: provider.id,
        serviceId: activeService.id, status: 'pending',
        paymentMethod: payMethod, quantity: qty,
      });
      if (payMethod === 'wallet') {
        await supabaseService.freezeWalletPayment({ clientId: user.id, amount: total, requestId: reqId });
      }
      setDialog(null); showSnack('تم إرسال الطلب بنجاح!');
    } catch (e) {
  const msg = e.message || '';
  if (msg.includes('PROVIDER_NO_PAYMENT_METHOD')) {
    showSnack('هذا المزود لا يقبل الدفع لا نقداً ولا عبر المحفظة حالياً', false);
  } else if (msg.includes('PAYMENT_METHOD_NOT_ACCEPTED')) {
    showSnack('طريقة الدفع هذه غير مقبولة عند هذا المزود', false);
  } else {
    showSnack('فشل إرسال الطلب: ' + msg, false);
  }
} finally { setSending(false); }
  }

  async function buyDigital() {
    if (purchased.has(activeService?.id)) { openPdf(activeService); return; }
    if (clientBalance < (activeService?.price ?? 0)) {
      showSnack(`رصيدك غير كافٍ (${fmt(clientBalance)} د.ج متاح)`, false); return;
    }
    setSending(true);
    try {
      await supabaseService.purchaseDigitalProduct({
        clientId: user.id, serviceId: activeService.id,
        providerId: provider.id, amount: activeService.price,
      });
      setPurchased(prev => new Set([...prev, activeService.id]));
      setDialog('success');
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('insufficient')) showSnack('رصيدك غير كافٍ', false);
      else if (msg.includes('already') || msg.includes('duplicate')) showSnack('اشتريت هذا المنتج مسبقاً', false);
      else showSnack(msg, false);
    } finally { setSending(false); }
  }

  function openPdf(service) {
    const fileId = service?.fileUrl || service?.file_url;
    if (!fileId) { showSnack('الملف غير متاح', false); return; }
    navigate('/read', {
      state: {
        pdfUrl:    getBackblazeDownloadUrl(fileId),
        title:     service?.name || 'كتاب',
        coverUrl:  service?.imageUrl || service?.image_url,
        author:    provider?.fullName || provider?.full_name,
        serviceId: service?.id,
      },
    });
  }

  function openPreview(service) {
    const productType = service?.productType || service?.product_type || 'pdf';
    const imgs = service?.previewImages || service?.preview_images || [];
    if (productType === 'link') {
      if (imgs.length > 0) { setActiveService(service); setActiveImg(0); setDialog('gallery'); }
      else showSnack('لا توجد معاينة متاحة', false);
      return;
    }
    if (productType === 'zip') {
      const prevUrl = service?.previewUrl || service?.preview_url;
      if (prevUrl) openPdfPreview(service);
      else if (imgs.length) { setActiveService(service); setActiveImg(0); setDialog('gallery'); }
      else showSnack('لا توجد معاينة متاحة', false);
      return;
    }
    openPdfPreview(service);
  }

  function openPdfPreview(service) {
    const fileId = service?.previewUrl || service?.preview_url;
    if (!fileId) { showSnack('لا توجد معاينة متاحة لهذا المنتج', false); return; }
    navigate('/read', {
      state: {
        pdfUrl:    getBackblazeDownloadUrl(fileId),
        title:     service?.name || 'معاينة',
        coverUrl:  service?.imageUrl || service?.image_url,
        author:    provider?.fullName || provider?.full_name,
        serviceId: service?.id,
        isPreview: true,
      },
    });
  }

  function hasAnyPreview(service) {
    return !!(service?.previewUrl || service?.preview_url) || (service?.previewImages || service?.preview_images || []).length > 0;
  }

  const isDigital   = provider?.isDigitalProducts || provider?.is_digital_products;
  const isStore     = provider?.isStore            || provider?.is_store;
  const acceptWallet= provider?.acceptsWallet      || provider?.accepts_wallet;
  const acceptCash  = provider?.acceptsCash        ?? provider?.accepts_cash ?? true;
  const avgRating   = provider?.averageRating      || provider?.average_rating || 0;
  const isVerified  = provider?.isVerified         || provider?.is_verified;
  const available   = provider?.available;

  /* ── Loading / Error ── */
  if (loading) return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex items-center justify-center" dir="rtl">
      <div className="w-8 h-8 border-2 border-neutral-200 dark:border-neutral-800 border-t-neutral-900 dark:border-t-white rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex flex-col items-center justify-center gap-4 p-8" dir="rtl">
      <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-900 rounded-2xl flex items-center justify-center text-2xl">👤</div>
      <p className="text-neutral-500 dark:text-neutral-400 text-sm text-center">{error}</p>
      <button
        onClick={load}
        className="px-5 py-2.5 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-sm font-bold rounded-xl hover:opacity-90 transition-opacity"
      >
        إعادة المحاولة
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 font-sans antialiased" dir="rtl">

      {/* App Banner - موبايل فقط */}
      {showAppBanner && isMobile && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={handleCloseBanner}
        >
          <div
            className="w-full bg-white dark:bg-neutral-900 rounded-t-3xl p-6 shadow-2xl border-t border-neutral-200 dark:border-neutral-800"
            style={{ maxWidth: 480 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="w-12 h-1 bg-neutral-300 dark:bg-neutral-700 rounded-full mx-auto mb-6" />

            {/* Logo + Title */}
            <div className="flex items-center gap-4 mb-6" dir="rtl">
              <div className="w-16 h-16 bg-neutral-900 dark:bg-white rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-white dark:text-neutral-900 font-black text-2xl">K</span>
              </div>
              <div>
                <h2 className="text-xl font-black text-neutral-900 dark:text-white">تطبيق Khidma</h2>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-0.5">
                  افتح في التطبيق للحصول على تجربة أفضل
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              {/* فتح في التطبيق */}
              <button
                onClick={handleOpenApp}
                className="flex items-center justify-center gap-2 w-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-center py-4 rounded-2xl font-bold text-base hover:opacity-90 transition-opacity"
              >
                <span>📱</span>
                <span>فتح في التطبيق</span>
              </button>

              {/* تحميل التطبيق */}
              <a
                href={playStoreLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 text-center py-4 rounded-2xl font-bold text-base no-underline hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
              >
                <span>⬇️</span>
                <span>تحميل التطبيق</span>
              </a>

              {/* الاستمرار في الموقع */}
              <button
                onClick={handleCloseBanner}
                className="w-full text-neutral-400 dark:text-neutral-500 text-center py-3 text-sm hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              >
                المتابعة في الموقع
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Snackbar ── */}
      {snack && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-lg transition-all ${
            snack.ok
              ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
              : 'bg-red-600 dark:bg-red-500 text-white'
          }`}
          style={{ animation: 'slideDown .25s ease' }}
        >
          <span>{snack.ok ? '✓' : '✕'}</span>
          {snack.msg}
        </div>
      )}

      {/* ── Hero ── */}
      <div className="relative bg-neutral-900 dark:bg-black overflow-hidden">
        {/* Blurred background */}
        {(provider?.avatarUrl || provider?.avatar_url) && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${provider?.avatarUrl || provider?.avatar_url})`,
              filter: 'grayscale(1) blur(32px) brightness(0.25)',
              transform: 'scale(1.1)',
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/90" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 z-10 w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/10 rounded-xl flex items-center justify-center text-white transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center pt-12 pb-8 px-5">
          <div className="relative mb-3">
            <img
              src={
                provider?.avatarUrl || provider?.avatar_url ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(provider?.fullName || 'P')}&background=1a1a1a&color=fff`
              }
              alt="avatar"
              className="w-20 h-20 rounded-2xl object-cover border-2 border-white/20"
              onError={e => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(provider?.fullName || 'P')}&background=1a1a1a&color=fff`;
              }}
            />
            {isVerified && (
              <span className="absolute -bottom-1 -left-1 w-6 h-6 bg-white rounded-full border-2 border-neutral-900 flex items-center justify-center text-neutral-900 text-xs font-bold">
                ✓
              </span>
            )}
          </div>

          <h1 className="text-xl font-black text-white mb-1">
            {provider?.fullName || provider?.full_name || '—'}
          </h1>
          <p className="text-sm text-white/50 mb-4">
            {provider?.professionName || '—'} · {provider?.stateName || '—'}
          </p>

          {/* Badges row */}
          <div className="flex flex-wrap justify-center gap-2">
            <Badge
              tone={available ? 'success' : 'danger'}
              dot={available ? '#fff' : '#fff'}
              label={available ? 'متاح' : 'غير متاح'}
              dark
            />
            <Badge tone="neutral" label={`★ ${avgRating.toFixed(1)}`} dark />
            {isVerified   && <Badge tone="neutral" label="موثّق" dark solid />}
            {acceptCash && acceptWallet && <Badge tone="neutral" label="يقبل الكاش والمحفظة" dark />}
            {acceptCash && !acceptWallet && <Badge tone="neutral" label="كاش فقط" dark />}
            {!acceptCash && acceptWallet && <Badge tone="neutral" label="محفظة فقط" dark />}
            {!acceptCash && !acceptWallet && <Badge tone="danger" label="لا يقبل الدفع" dark />}
            {isStore      && <Badge tone="neutral" label="متجر" dark />}
            {isDigital    && <Badge tone="neutral" label="منتجات رقمية" dark />}
            <Badge tone="neutral" label={`ID: ${provider?.providerId || provider?.provider_id || '—'}`} dark />
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">

        {/* Fav Button */}
        {isClient && (
          <button
            onClick={toggleFav}
            disabled={favLoading}
            className={`w-full h-11 flex items-center justify-center gap-2 rounded-xl border text-sm font-bold transition-all ${
              isFav
                ? 'bg-neutral-900 dark:bg-white border-neutral-900 dark:border-white text-white dark:text-neutral-900'
                : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800'
            }`}
          >
            {favLoading
              ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              : <>
                  <span className="text-base">{isFav ? '🔖' : '🔖'}</span>
                  <span>{isFav ? 'محفوظ في المفضلة' : 'حفظ في المفضلة'}</span>
                </>
            }
          </button>
        )}

        {/* Services */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-800">
            <h2 className="text-sm font-bold text-neutral-900 dark:text-white">
              {isDigital ? `المنتجات الرقمية (${services.length})` : `الخدمات (${services.length})`}
            </h2>
          </div>

          {services.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-neutral-400 dark:text-neutral-600">
              <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex items-center justify-center text-xl">
                {isDigital ? '📚' : '🔧'}
              </div>
              <p className="text-sm">{isDigital ? 'لا توجد منتجات متاحة' : 'لا توجد خدمات متاحة'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
              {services.map((s, i) => (
                <div
                  key={s.id || i}
                  className="rounded-xl border border-neutral-100 dark:border-neutral-800 overflow-hidden"
                >
                  {isDigital ? (
                    <DigitalCard
                      service={s}
                      provider={provider}
                      isPurchased={purchased.has(s.id)}
                      isClient={isClient}
                      hasPreview={hasAnyPreview(s)}
                      onBuy={() => { setActiveService(s); setDialog('buy'); }}
                      onRead={() => openPdf(s)}
                      onPreview={() => openPreview(s)}
                    />
                  ) : (
                    <ServiceCard
                      service={s}
                      provider={provider}
                      isStore={isStore}
                      isClient={isClient}
                      onRequest={() => confirmRequest(s)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Dialogs ── */}
      {dialog === 'request' && activeService && (
        <Overlay onClose={() => setDialog(null)}>
          <RequestDialog
            service={activeService}
            provider={provider}
            isStore={isStore}
            acceptWallet={acceptWallet}
            acceptCash={acceptCash}
            qty={qty} setQty={setQty}
            payMethod={payMethod} setPayMethod={setPayMethod}
            sending={sending}
            onCancel={() => setDialog(null)}
            onConfirm={sendRequest}
          />
        </Overlay>
      )}

      {dialog === 'buy' && activeService && (
        <Overlay onClose={() => setDialog(null)}>
          <BuySheet
            service={activeService}
            provider={provider}
            balance={clientBalance}
            sending={sending}
            onCancel={() => setDialog(null)}
            onConfirm={buyDigital}
          />
        </Overlay>
      )}

      {dialog === 'success' && activeService && (
        <Overlay onClose={() => setDialog(null)}>
          <SuccessSheet
            service={activeService}
            onClose={() => setDialog(null)}
            onRead={() => { setDialog(null); openPdf(activeService); }}
          />
        </Overlay>
      )}

      {dialog === 'gallery' && activeService && (
        <GalleryScreen
          images={activeService?.previewImages || activeService?.preview_images || []}
          title={activeService?.name || 'معاينة'}
          providerName={provider?.fullName || provider?.full_name}
          current={activeImg}
          setCurrent={setActiveImg}
          onClose={() => setDialog(null)}
        />
      )}

      {/* ── Login Required Dialog ── */}
      {loginRequiredDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setLoginRequiredDialog(false)}
        >
          <div
            className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6 w-full max-w-sm text-center shadow-xl"
            style={{ animation: 'slideUp .3s cubic-bezier(.16,1,.3,1)' }}
          >
            <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-2xl flex items-center justify-center text-3xl mb-4 mx-auto">
              🔐
            </div>
            <h3 className="text-lg font-black text-neutral-900 dark:text-white mb-2">يجب تسجيل الدخول أولاً</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 leading-relaxed">
              لكي تتمكن من إرسال طلب لهذا المزود، يجب عليك إنشاء حساب أو تسجيل الدخول. سيتم إرجاعك إلى هذه الصفحة تلقائياً بعد الدخول.
            </p>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => {
                  setLoginRequiredDialog(false);
                  navigate('/auth');
                }}
                className="w-full h-11 rounded-xl bg-neutral-900 dark:bg-white hover:opacity-90 text-white dark:text-neutral-900 text-sm font-bold transition-opacity flex items-center justify-center gap-2"
              >
                <span>🔑</span>
                <span>تسجيل الدخول / إنشاء حساب</span>
              </button>
              <button
                onClick={() => setLoginRequiredDialog(false)}
                className="w-full h-11 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 text-sm font-bold hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity:0; transform: translateX(-50%) translateY(-8px); }
          to   { opacity:1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ── Badge ──
   Monochrome badge system: neutral = quiet gray chip, success/danger keep a
   restrained semantic tint so status is still scannable at a glance. */
function Badge({ tone = 'neutral', label, dark = false, solid = false }) {
  const toneClasses = {
    neutral: dark
      ? 'bg-white/10 text-white/90 border border-white/10'
      : 'bg-neutral-100 text-neutral-700 border border-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700',
    success: dark
      ? 'bg-white/15 text-white border border-white/20'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20',
    danger: dark
      ? 'bg-white/10 text-white/80 border border-white/10'
      : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
        solid && dark ? 'bg-white text-neutral-900 border border-white' : toneClasses[tone]
      }`}
    >
      {label}
    </span>
  );
}

/* ── Overlay ── */
function Overlay({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center p-3"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-lg"
        style={{ animation: 'slideUp .3s cubic-bezier(.16,1,.3,1)' }}
      >
        {children}
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity:0; }
          to   { transform: translateY(0);    opacity:1; }
        }
      `}</style>
    </div>
  );
}

/* ── Digital Card ── */
function DigitalCard({ service, isPurchased, isClient, hasPreview, onBuy, onRead, onPreview }) {
  const hasCover  = !!(service?.imageUrl || service?.image_url);
  const rating    = service?.averageRating || service?.average_rating || 0;
  const media     = buildMediaList(service);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      {hasCover && (
        <div className="relative w-full aspect-[3/4] overflow-hidden bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-100 dark:border-neutral-800">
          <MediaCarousel
            media={media}
            title={service?.name}
            height="h-full"
            objectFit="object-cover"
            showDots={media.length > 1}
            showArrows={media.length > 1}
            lightboxEnabled={false}
          />
          {isPurchased && (
            <div className="absolute inset-0 bg-neutral-900/60 dark:bg-black/60 flex items-center justify-center pointer-events-none">
              <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-neutral-900 text-sm font-bold">✓</span>
            </div>
          )}
          {rating > 0 && (
            <span className="absolute top-2 left-2 text-[11px] font-bold bg-white/90 text-neutral-900 px-2 py-0.5 rounded-md z-10">
              ★ {rating.toFixed(1)}
            </span>
          )}
        </div>
      )}

      <div className="flex-1 flex flex-col p-3">
        <p className="text-sm font-bold text-neutral-900 dark:text-white leading-snug line-clamp-1 mb-0.5">{service?.name}</p>
        {!hasCover && rating > 0 && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400 font-semibold mb-1">★ {rating.toFixed(1)}</span>
        )}
        {service?.description && (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 line-clamp-2 leading-relaxed">{service?.description}</p>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
          <span className="text-sm font-black text-neutral-900 dark:text-white whitespace-nowrap">{fmt(service?.price)} د.ج</span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {hasPreview && (
              <button
                onClick={onPreview}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors font-semibold"
              >
                معاينة
              </button>
            )}
            {(isClient || true) && (
              <button
                onClick={isPurchased ? onRead : onBuy}
                className="text-xs px-3 py-1.5 rounded-lg bg-neutral-900 dark:bg-white hover:opacity-90 text-white dark:text-neutral-900 font-bold transition-opacity"
              >
                {isPurchased ? 'قراءة' : 'شراء'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Service Card ── */
function ServiceCard({ service, isStore, isClient, onRequest }) {
  const hasCover  = !!(service?.imageUrl || service?.image_url);
  const rating    = service?.averageRating || service?.average_rating || 0;
  const inStock   = (service?.stock ?? 1) > 0;
  const hasStock  = service?.stock != null;
  const media     = buildMediaList(service);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      {hasCover && (
        <div className="relative w-full aspect-[4/3] overflow-hidden bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-100 dark:border-neutral-800">
          <MediaCarousel
            media={media}
            title={service?.name}
            height="h-full"
            objectFit="object-cover"
            showDots={media.length > 1}
            showArrows={media.length > 1}
            lightboxEnabled={false}
          />
          {isStore && (
            <span className="absolute top-2 right-2 text-[11px] font-bold bg-black/70 text-white px-2 py-0.5 rounded-md backdrop-blur-sm z-10">
              متجر
            </span>
          )}
          {rating > 0 && (
            <span className="absolute top-2 left-2 text-[11px] font-bold bg-white/90 text-neutral-900 px-2 py-0.5 rounded-md z-10">
              ★ {rating.toFixed(1)}
            </span>
          )}
          {isStore && hasStock && (
            <span
              className={`absolute bottom-2 left-2 text-[11px] font-bold px-2 py-0.5 rounded-md z-10 ${
                inStock ? 'bg-white/90 text-neutral-900' : 'bg-black/70 text-white'
              }`}
            >
              {inStock ? `${service.stock} متوفر` : 'نفذ'}
            </span>
          )}
        </div>
      )}

      <div className="flex-1 flex flex-col p-3">
        <div className="flex items-start gap-2">
          {!hasCover && (
            <div className="w-9 h-9 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center text-base flex-shrink-0">
              {isStore ? '📦' : '🔧'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-bold text-neutral-900 dark:text-white line-clamp-1">{service?.name}</span>
              {!hasCover && rating > 0 && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400 font-semibold flex-shrink-0">★ {rating.toFixed(1)}</span>
              )}
            </div>
            {service?.description && (
              <p className="text-xs text-neutral-400 dark:text-neutral-500 line-clamp-2 leading-relaxed">{service?.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-black text-neutral-900 dark:text-white whitespace-nowrap">{fmt(service?.price)} د.ج</span>
            {isStore && hasStock && !hasCover && (
              <span
                className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border whitespace-nowrap ${
                  inStock
                    ? 'text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700'
                    : 'text-neutral-400 dark:text-neutral-600 border-neutral-200 dark:border-neutral-800'
                }`}
              >
                {inStock ? `${service.stock} متوفر` : 'نفذ'}
              </span>
            )}
          </div>
          <button
            onClick={onRequest}
            className="text-xs px-3 py-1.5 rounded-lg bg-neutral-900 dark:bg-white hover:opacity-90 text-white dark:text-neutral-900 font-bold transition-opacity active:scale-95 flex-shrink-0"
          >
            طلب
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Request Dialog ── */
function RequestDialog({ service, isStore, acceptWallet, acceptCash, qty, setQty, payMethod, setPayMethod, sending, onCancel, onConfirm }) {
  const maxQty = isStore ? (service?.stock ?? 1) : 1;
  const total  = (service?.price ?? 0) * qty;

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-5 shadow-xl">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex items-center justify-center text-lg">📤</div>
        <div>
          <p className="text-base font-black text-neutral-900 dark:text-white">تأكيد الطلب</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">"{service?.name}"</p>
        </div>
      </div>

      {isStore && maxQty > 1 && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">الكمية</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setQty(q => Math.max(1, q - 1))}
              disabled={qty <= 1}
              className="w-8 h-8 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-white font-bold text-lg flex items-center justify-center disabled:opacity-30 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            >−</button>
            <span className="text-base font-black text-neutral-900 dark:text-white w-6 text-center">{qty}</span>
            <button
              onClick={() => setQty(q => Math.min(maxQty, q + 1))}
              disabled={qty >= maxQty}
              className="w-8 h-8 rounded-lg border border-neutral-900 dark:border-white bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-bold text-lg flex items-center justify-center disabled:opacity-30 hover:opacity-90 transition-opacity"
            >+</button>
          </div>
        </div>
      )}

      <div className="bg-neutral-50 dark:bg-neutral-800/60 rounded-xl p-3 text-center mb-4 border border-neutral-100 dark:border-neutral-800">
        {qty > 1 && <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-0.5">{fmt(service?.price)} × {qty}</p>}
        <p className="text-2xl font-black text-neutral-900 dark:text-white">{fmt(total)} <span className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">د.ج</span></p>
      </div>

      <p className="text-xs font-bold text-neutral-500 dark:text-neutral-400 mb-2">طريقة الدفع</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => acceptCash && setPayMethod('cash')}
          disabled={!acceptCash}
          className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-sm font-bold transition-all ${
            !acceptCash
              ? 'border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 text-neutral-300 dark:text-neutral-700 cursor-not-allowed'
              : payMethod === 'cash'
                ? 'border-neutral-900 dark:border-white bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                : 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-500'
          }`}
        >
          <span>💵</span>
          <span>{acceptCash ? 'كاش' : 'غير متاح'}</span>
        </button>
        <button
          onClick={() => acceptWallet && setPayMethod('wallet')}
          disabled={!acceptWallet}
          className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-sm font-bold transition-all ${
            !acceptWallet
              ? 'border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 text-neutral-300 dark:text-neutral-700 cursor-not-allowed'
              : payMethod === 'wallet'
                ? 'border-neutral-900 dark:border-white bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                : 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-500'
          }`}
        >
          <span>👛</span>
          <span>{acceptWallet ? 'رصيد المحفظة' : 'غير متاح'}</span>
        </button>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 h-11 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 text-sm font-bold hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          إلغاء
        </button>
        <button
          onClick={onConfirm}
          disabled={sending}
          className="flex-[2] h-11 rounded-xl bg-neutral-900 dark:bg-white hover:opacity-90 text-white dark:text-neutral-900 text-sm font-bold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
        >
          {sending ? <div className="w-4 h-4 border-2 border-white/30 dark:border-neutral-900/30 border-t-white dark:border-t-neutral-900 rounded-full animate-spin" /> : 'تأكيد'}
        </button>
      </div>
    </div>
  );
}

/* ── Buy Sheet ── */
function BuySheet({ service, provider, balance, sending, onCancel, onConfirm }) {
  const price  = service?.price ?? 0;
  const enough = balance >= price;

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-5 shadow-xl">
      <div className="w-8 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto mb-4" />

      <div className="flex flex-col items-center mb-4">
        <div className="w-16 h-16 rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-800 mb-3">
          {service?.imageUrl || service?.image_url
            ? <img src={service?.imageUrl || service?.image_url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-2xl">📚</div>
          }
        </div>
        <p className="text-base font-black text-neutral-900 dark:text-white text-center max-w-xs line-clamp-2">{service?.name}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">{provider?.fullName || provider?.full_name}</p>
      </div>

      <div className={`rounded-xl border p-4 mb-3 ${
        !enough
          ? 'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/5'
          : 'border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/60'
      }`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-lg">👛</div>
          <div className="flex-1">
            <p className="text-sm font-bold text-neutral-900 dark:text-white">محفظة Kinpay</p>
            <p className={`text-xs font-semibold ${enough ? 'text-neutral-400 dark:text-neutral-500' : 'text-red-500 dark:text-red-400'}`}>
              متاح: {fmt(balance)} د.ج
            </p>
          </div>
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
            enough
              ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
              : 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'
          }`}>
            {enough ? '✓' : '✕'}
          </div>
        </div>
        <div className="h-px bg-neutral-200 dark:bg-neutral-700 mb-3" />
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">المبلغ</span>
          <span className="text-2xl font-black text-neutral-900 dark:text-white">{fmt(price)} <span className="text-sm font-semibold text-neutral-400 dark:text-neutral-500">د.ج</span></span>
        </div>
        {!enough && (
          <div className="mt-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400 font-semibold">
            <span>⚠</span>
            <span>تحتاج {fmt(price - balance)} د.ج إضافية</span>
          </div>
        )}
      </div>

      <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center mb-4">يُخصم من رصيدك فوراً · يُحفظ في مكتبتك للقراءة في أي وقت</p>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 h-11 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 text-sm font-bold hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          إلغاء
        </button>
        <button
          onClick={onConfirm}
          disabled={!enough || sending}
          className="flex-[2] h-11 rounded-xl bg-neutral-900 dark:bg-white hover:opacity-90 text-white dark:text-neutral-900 text-sm font-bold flex items-center justify-center gap-2 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending
            ? <div className="w-4 h-4 border-2 border-white/30 dark:border-neutral-900/30 border-t-white dark:border-t-neutral-900 rounded-full animate-spin" />
            : <><span>👛</span> دفع {fmt(price)} د.ج</>
          }
        </button>
      </div>
    </div>
  );
}

/* ── Success Sheet ── */
function SuccessSheet({ service, onClose, onRead }) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6 flex flex-col items-center text-center shadow-xl">
      <div
        className="w-16 h-16 bg-neutral-900 dark:bg-white rounded-2xl flex items-center justify-center text-white dark:text-neutral-900 text-2xl font-black mb-4"
        style={{ animation: 'popIn .5s cubic-bezier(.16,1,.3,1)' }}
      >
        ✓
      </div>
      <p className="text-lg font-black text-neutral-900 dark:text-white mb-1">تم الشراء بنجاح!</p>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{service?.name}</p>
      <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-6">يمكنك قراءته الآن أو من مكتبتك في أي وقت</p>
      <div className="flex gap-3 w-full">
        <button
          onClick={onClose}
          className="flex-1 h-11 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 text-sm font-bold hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          لاحقاً
        </button>
        <button
          onClick={onRead}
          className="flex-[2] h-11 rounded-xl bg-neutral-900 dark:bg-white hover:opacity-90 text-white dark:text-neutral-900 text-sm font-bold transition-opacity"
        >
          قراءة الآن
        </button>
      </div>
      <style>{`
        @keyframes popIn {
          from { transform: scale(0.5); opacity:0; }
          to   { transform: scale(1);   opacity:1; }
        }
      `}</style>
    </div>
  );
}

/* ── Gallery Screen ── */
function GalleryScreen({ images, title, providerName, current, setCurrent, onClose }) {
  const [scale, setScale] = useState(1);

  function prev() { setCurrent(c => Math.max(0, c - 1)); }
  function next() { setCurrent(c => Math.min(images.length - 1, c + 1)); }

  useEffect(() => {
    const handler = e => {
      if (e.key === 'ArrowLeft')  next();
      if (e.key === 'ArrowRight') prev();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Bar */}
      <div className="flex items-center gap-3 px-4 pt-12 pb-3 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-10" dir="rtl">
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-white text-sm hover:bg-white/20 transition-colors"
        >
          ✕
        </button>
        <div className="flex-1">
          <p className="text-sm font-bold text-white">{title}</p>
          {providerName && <p className="text-xs text-white/50">{providerName}</p>}
        </div>
        <span className="text-xs text-white/60 bg-white/10 px-2.5 py-1 rounded-full">
          {current + 1} / {images.length}
        </span>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center p-12 relative overflow-hidden">
        {images[current] && (
          <img
            src={images[current]}
            alt=""
            className="max-w-full max-h-full object-contain"
            style={{ transform: `scale(${scale})`, transition: 'transform .2s' }}
            onError={e => { e.target.src = ''; }}
          />
        )}
        {/* Zoom */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {[{l:'+', fn:() => setScale(s => Math.min(4, s+0.5))}, {l:'↺', fn:() => setScale(1)}, {l:'−', fn:() => setScale(s => Math.max(0.5, s-0.5))}].map(({l,fn}) => (
            <button key={l} onClick={fn} className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-bold hover:bg-white/20 transition-colors flex items-center justify-center">{l}</button>
          ))}
        </div>
      </div>

      {/* Nav */}
      {images.length > 1 && current > 0 && (
        <button onClick={prev} className="absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-xl font-bold hover:bg-white/20 transition-colors flex items-center justify-center">›</button>
      )}
      {images.length > 1 && current < images.length - 1 && (
        <button onClick={next} className="absolute top-1/2 -translate-y-1/2 left-3 w-10 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-xl font-bold hover:bg-white/20 transition-colors flex items-center justify-center">‹</button>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => (
            <span
              key={i}
              onClick={() => setCurrent(i)}
              className="cursor-pointer rounded-full transition-all"
              style={{
                width: i === current ? 20 : 6,
                height: 6,
                background: i === current ? '#fff' : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}